import RiQuestionLine from '@ben/icons/question-line';
import Checkbox from '@ben/orion/checkbox';
import RIcon from '@ben/orion/remix-icon';
import { Tooltip, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import Button, { ButtonVariant } from '../../../deprecated-button/button';
import { Input, InputVariant } from '../../../input';

export interface EditableFile {
  originalName: string;
  uuid: string;
  file: string;
  description?: string;
  name: string;
  isPolicyDocument?: boolean;
}

interface FileEditingContainerProps {
  file: EditableFile;
  index: number;
  onCancel: (index: number) => void;
  onSave: (index: number, fileChanges: Partial<EditableFile>) => void;
}

const FileEditingContainer = ({
  file,
  index,
  onCancel,
  onSave,
}: FileEditingContainerProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [name, setName] = useState(file.name);
  const [description, setDescription] = useState(file.description);
  const [isPolicyDocument, setIsPolicyDocument] = useState(
    file.isPolicyDocument || false
  );

  const handleSave = () => {
    if (name) {
      onSave(index, { name, description, isPolicyDocument });
      onCancel(index);
    }
  };

  return (
    <Container>
      <Input
        variant={InputVariant.Text}
        name={t('documents.documentName', {
          defaultValue: 'Document name',
        })}
        value={name}
        width="100%"
        onChange={(event) => {
          setName(event.target.value);
        }}
        hasError={!name}
        errorMessage={t('phrases.requiredField', {
          defaultValue: 'This field cannot be blank',
        })}
        maxLength={50}
      />
      <Input
        variant={InputVariant.TextArea}
        name={t('documents.documentDescription', {
          defaultValue: 'Document description (optional)',
        })}
        width="100%"
        height="85px"
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
        maxLength={70}
      />
      <CheckboxContainer>
        <Checkbox
          value={isPolicyDocument}
          label={t('documents.internalDocument')}
          onChange={setIsPolicyDocument}
        />
        <Tooltip
          w={300}
          multiline
          label={t('documents.internalDocumentTooltip')}
        >
          <UnstyledButton
            aria-label={t('documents.internalDocumentTooltip')}
            w={12}
          >
            <RIcon
              icon={RiQuestionLine}
              size="xs"
              color={theme.palette.neutral[80]}
            />
          </UnstyledButton>
        </Tooltip>
      </CheckboxContainer>
      <ButtonContainer>
        <Button
          variant={ButtonVariant.secondary}
          label={t('words.cancel', {
            defaultValue: 'Cancel',
          })}
          onClick={() => onCancel(index)}
          width="auto"
        />
        <Button
          variant={ButtonVariant.primary}
          label={t('words.save', {
            defaultValue: 'Save',
          })}
          onClick={handleSave}
          width="auto"
        />
      </ButtonContainer>
    </Container>
  );
};

export default FileEditingContainer;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    margin-top: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(2)};
  `}
`;

const ButtonContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;

const CheckboxContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(1)};
    align-items: center;
  `}
`;
